import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable } from 'jotai/utils';
import { atomWithQuery } from 'jotai-tanstack-query';
import { Temporal } from 'temporal-polyfill';
import { IFundMetricsResponseDataModel } from '../../../data-models/fund-metrics.data-model';
import { IMetricsDataModel } from '../../../data-models/metrics.data-model';
import { companyMapAtom } from '../../../services/hooks/useSaveCompanyData';
import { formulaFieldsListLoadable } from '../../../services/state/AdminPanel/FormulaFieldsStateJ';
import { applyFormulaFields, formulaFieldsForEntity } from '../../../util/formula-fields';
import {
  fetchMetrics,
  getFundMetrics,
  getPlainDateString,
} from '../../../services/queries/MaggieMetricsQueries';
import { isLocal } from '../../../util/environment';
import { FeatureFlags } from '../../../util/feature-flags';
import { getLdFlag } from '../../../util/ld-client';
import {
  CalculatedMetricsDataModel,
  MetricsGroupColumns,
} from '../components/OverviewTable/MetricsCalculator';
import { selectedViewDatePFJ } from './PageStateJ';
import { rowGroupColumnsState } from './UIStateJ';

export const MetricsQueryKey = 'metricsQuery';

// Grid's normal metrics load. Request must stay byte-identical to before Fund Family
// existed (flag on or off) — requesting calculateFundTypeData here would make the api skip
// its pre-compressed Brotli fast path for every legal-structure tenant on both grid dates.
export const metricsResponseByDateAtom = atomFamily(
  ({ date, currency }: { date: Temporal.PlainDate; currency: string }) =>
    atomWithQuery(() => ({
      queryKey: [MetricsQueryKey, date.toString(), currency],
      queryFn: () => {
        return fetchMetrics(date.toString(), currency);
      },
      staleTime: Infinity,
      throwOnError: true,
    })),
  (a, b) => {
    return a.date.equals(b.date) && a.currency === b.currency;
  }
);

/**
 * Lazy sibling of {@link metricsResponseByDateAtom}: only the family custom-field overlay needs
 * `familyData`, so this is the one place `calculateFundTypeData: true` is requested, gated on
 * `rowGroupColumnsState` so it only fires while the grid is actually grouped by Fund Family.
 * Same atomFamily key shape (date/currency) as the sibling on purpose — the key omits the
 * grouping/flag gate, so a grid that flips group-by or an LD flag flip mid-session is picked up
 * on the next read (enabled is re-evaluated reactively), but a stale cached response for an
 * unchanged date/currency is not invalidated by a flag flip alone; a reload covers that case.
 */
export const familyMetricsResponseByDateAtom = atomFamily(
  ({ date, currency }: { date: Temporal.PlainDate; currency: string }) =>
    atomWithQuery((get) => {
      const groupedByFamily = get(rowGroupColumnsState).some(
        (column) => column.colId === MetricsGroupColumns.FundFamily
      );
      // Same gate as useEntityManagementEnabled; read outside React via getLdFlag since this
      // queryFn has no hook context.
      const familyDataEnabled = isLocal() || getLdFlag(FeatureFlags.legalStructureEnabled, false);
      return {
        queryKey: [MetricsQueryKey, 'family', date.toString(), currency],
        queryFn: () => fetchMetrics(date.toString(), currency, true),
        enabled: groupedByFamily && familyDataEnabled,
        staleTime: Infinity,
        throwOnError: true,
      };
    }),
  (a, b) => {
    return a.date.equals(b.date) && a.currency === b.currency;
  }
);

export const metricsByDateAtom = atomFamily(
  ({ date, currency }: { date: Temporal.PlainDate; currency: string }) => {
    return atom(async (get) => {
      const { data } = get(metricsResponseByDateAtom({ date, currency }));
      if (!data?.metrics) {
        return data?.metrics;
      }
      // Materialize formula-field values on the base rows so the ungrouped grid
      // and every atom-reading consumer (charts, KPIs) see them. Loadable read:
      // a failed formula fetch must not take the metrics page down. Only
      // company formulas evaluate against metrics rows.
      const formulaLoad = get(formulaFieldsListLoadable);
      const formulaFields = formulaFieldsForEntity(
        formulaLoad.state === 'hasData' ? (formulaLoad.data ?? []) : [],
        'company'
      );
      const companyMap = get(companyMapAtom);
      return applyFormulaFields(data.metrics, formulaFields, (row) => companyMap.get(row.companyId), {
        asOfDate: date.toString(),
      });
    });
  },
  (a, b) => a.date.equals(b.date) && a.currency === b.currency
);

export interface IFilteredMetricsPublish {
  metrics: IMetricsDataModel[];
  /** The fund scoping `metrics` was built under; `null` = no fund filter. */
  fundIds: number[] | null;
}

/**
 * The grid's published rows together with the fund basis that produced them, written as ONE value.
 * Rows and fund selection reach React on different lanes, so publishing them separately lets a
 * render pair one basis' rows with another's scoping — the FD-4341 flash. A single value cannot
 * tear: every reader sees rows and basis from the same publish.
 */
export const filteredMetricsPublishAtom = atomFamily((_viewId: number) => {
  return atom<IFilteredMetricsPublish>({ metrics: [], fundIds: null });
});

export const metricsFilteredByViewAtom = atomFamily((viewId: number) => {
  return atom((get) => get(filteredMetricsPublishAtom(viewId)).metrics);
});

/** Plain date the grid's last published rows were built for; null until a grid has said. */
export const publishedAsOfDateByViewAtom = atomFamily((_viewId: number) => atom<string | null>(null));

// False between a date change and the grid's next publish, when the published rows still belong to
// the previous date. Nothing published yet counts as current, so a grid that never stamps is not held.
export const isPublishedForSelectedDateAtom = atomFamily((viewId: number) =>
  atom((get) => {
    const published = get(publishedAsOfDateByViewAtom(viewId));
    return published === null || published === getPlainDateString(get(selectedViewDatePFJ));
  })
);

export const metricsFromFilteredDataAtom = atom([] as CalculatedMetricsDataModel[]);

/**
 * Increment to force `fundMetricsByDateStateJ` (and downstream callers like
 * `selectedFundMetricsStateFP`) to refetch.
 */
export const forceFundMetricsUpdateJ = atom<number>(0);

/**
 * Async derived atom: reads {@link selectedViewDatePFJ} and re-evaluates when
 * {@link forceFundMetricsUpdateJ} ticks.
 */
export const fundMetricsByDateStateJ = atom(async (get): Promise<IFundMetricsResponseDataModel> => {
  get(forceFundMetricsUpdateJ);
  const selectedDate = get(selectedViewDatePFJ);
  return getFundMetrics(getPlainDateString(selectedDate));
});

export const fundMetricsByDateStateLoadableJ = loadable(fundMetricsByDateStateJ);

/**
 * `atomFamily` keyed by viewId; default derives from `fundMetricsByDateStateJ`
 * and is the full fund-id list. Consumers (the funds-overview grid) overwrite
 * this with the filtered list as the user filters the grid.
 */
export const fundMetricsFilteredFundIdsStateJ = atomFamily((_viewId: number) =>
  atomWithDefault<number[] | Promise<number[]>>(async (get) => {
    const metrics = await get(fundMetricsByDateStateJ);
    return metrics.metrics.map((m) => m.fundId);
  })
);
